import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import type { AnyFormApi } from '@tanstack/react-form';

import { useAppDispatch, useAppSelector } from '@/store';
import { showSuccessToast } from '@/utils/toast.utils';
import { selectIsCreateSixModal } from '@/store/selectors/ui';
import { hideModal } from '@/store/thunks/ui/uiThunk';
import { resetCreateSixState, setDraftField, setStep } from '@/store/slices/createSix/createSixSlice';
import {
  selectDraft,
  selectEditSixId,
  selectExternalUsers,
  selectInternalUsers,
  selectStep,
  selectSubmitting,
} from '@/store/selectors/createSix';
import { selectCurrentUserId } from '@/store/selectors/auth';
import { selectCatalogSixes } from '@/store/selectors/sixCatalog';
import { selectParticipants } from '@/store/selectors/sixDetail';
import { fetchCreateSixModalInformationThunk } from '@/store/thunks/create-six/fetchCreateSixModalInformation';
import { createSixThunk } from '@/store/thunks/create-six/createSixThunk';
import { updateSixThunk } from '@/store/thunks/create-six/updateSixThunk';
import { validateSixNameThunk } from '@/store/thunks/create-six/validateSixThunk';
import {
  CREATE_SIX_WIZARD_STEPS,
  resolveWizardStepNumber,
  resolveWizardStepStatus,
} from '@/utils/createSixWizard.utils';
import type { CreateExternalUserPayload, CreateSixPayload, CreateSixStep, LocalDraft } from '@/types/create-six.types';
import { createSixSchema } from '@/schemas/createSix.schema';
import {
  CREATE_SIX_DESCRIPTION_MAX_LENGTH,
  CREATE_SIX_NAME_MAX_LENGTH,
  MIN_LEN,
} from '@/constants';
import { createNewExternalUserThunk } from '@/store/thunks/user/createNewExternalUserThunk';
import { updateCreateSixParticipantsThunk } from '@/store/thunks/create-six/updateCreateSixParticipantsThunk';

function resolveNextStep(currentStep: CreateSixStep): CreateSixStep {
  if (currentStep === 'Basics') return 'Owners';
  if (currentStep === 'Owners') return 'Members';
  if (currentStep === 'Members') return 'External';
  return 'Review';
}

function resolvePreviousStep(currentStep: CreateSixStep): CreateSixStep {
  if (currentStep === 'Owners') return 'Basics';
  if (currentStep === 'Members') return 'Owners';
  if (currentStep === 'External') return 'Members';
  return 'External';
}

function buildCreateSixPayloadFromDraft(draft: ReturnType<typeof selectDraft>): CreateSixPayload {
  return {
    name: draft.name,
    description: draft.description,
    ownerIds: draft.ownerIds,
    internalParticipantIds: draft.internalParticipantIds,
    externalParticipantIds: draft.externalParticipantIds,
  };
}

function extractNewExternalUsersFromDraft(draft: ReturnType<typeof selectDraft>): CreateExternalUserPayload[] {
  return draft.newExternalUsers.map((user) => ({
    name: user.name,
    email: user.email,
    phone: user.phoneNumber,
    departmentOrganization: user.department ?? '',
  }));
}
function resolveIsNextDisabled(step: CreateSixStep, draft: LocalDraft): boolean {
  if (step === 'Basics') {
    return (
      draft.name.trim().length === MIN_LEN
      || draft.name.length > CREATE_SIX_NAME_MAX_LENGTH
      || draft.description.length > CREATE_SIX_DESCRIPTION_MAX_LENGTH
    );
  }
  if (step === 'Owners') {
    return draft.ownerIds.length === MIN_LEN;
  }
  return false;
}

interface UseCreateSixModalState {
  isOpen: boolean;
  isEditMode: boolean;
  isExternalConfirmationOpen: boolean;
  createSixStep: CreateSixStep;
  isSubmitting: boolean;
  draft: ReturnType<typeof selectDraft>;
  wizardSteps: Array<{
    stepNumber: number;
    label: string;
    status: ReturnType<typeof resolveWizardStepStatus>;
    isDisabled: boolean;
  }>;
  currentWizardStepNumber: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  isNextDisabled: boolean;
  form: AnyFormApi;
}

interface UseCreateSixModalHandlers {
  handleClose: () => void;
  handleCloseExternalConfirmation: () => void;
  handleConfirmExternalConfirmation: () => void;
  handlePrevious: () => void;
  handleNext: () => void;
}

interface UseCreateSixModalReturn {
  state: UseCreateSixModalState;
  handlers: UseCreateSixModalHandlers;
}

export function useCreateSixModal(): UseCreateSixModalReturn {
  const dispatch = useAppDispatch();

  const isOpen = useAppSelector(selectIsCreateSixModal);
  const createSixStep = useAppSelector(selectStep);
  const isSubmitting = useAppSelector(selectSubmitting);
  const draft = useAppSelector(selectDraft);
  const hasPendingExternalManualEntries = useAppSelector((state) => state.createSix.hasPendingExternalManualEntries);
  const internalUsers = useAppSelector(selectInternalUsers);
  const externalUsers = useAppSelector(selectExternalUsers);
  const editSixId = useAppSelector(selectEditSixId);
  const catalogSixes = useAppSelector(selectCatalogSixes);
  const participants = useAppSelector(selectParticipants);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const isEditMode = editSixId !== undefined;
  const [isExternalConfirmationOpen, setIsExternalConfirmationOpen] = useState(false);

  const defaultValues = useMemo<LocalDraft>(
    () => ({
      name: draft.name,
      description: draft.description,
      ownerIds: draft.ownerIds,
      internalParticipantIds: draft.internalParticipantIds,
      externalParticipantIds: draft.externalParticipantIds,
      newExternalUsers: draft.newExternalUsers,
    }),
    [draft]
  );

  const form = useForm({
    defaultValues,
    validators: {
      onSubmit: createSixSchema,
    },
  });

  // Keep form values in sync with Redux draft
  useEffect(() => {
    form.setFieldValue('name', draft.name);
    form.setFieldValue('description', draft.description);
    form.setFieldValue('ownerIds', draft.ownerIds);
    form.setFieldValue('internalParticipantIds', draft.internalParticipantIds);
    form.setFieldValue('externalParticipantIds', draft.externalParticipantIds);
    form.setFieldValue('newExternalUsers', draft.newExternalUsers);
  }, [draft, form]);

  // Hydrate draft from catalog + participants when opening in edit mode
  useEffect(() => {
    if (!isOpen || !isEditMode) return;

    const sixToEdit = catalogSixes.find((six) => String(six.id) === editSixId);
    if (sixToEdit === undefined) return;

    dispatch(setDraftField({ key: 'name', value: sixToEdit.name }));
    dispatch(setDraftField({ key: 'description', value: sixToEdit.description }));

    if (participants !== undefined) {
      dispatch(setDraftField({ key: 'ownerIds', value: participants.owners.map((owner) => owner.id) }));
      dispatch(setDraftField({ key: 'internalParticipantIds', value: participants.internalParticipants.map((p) => p.id) }));
      dispatch(setDraftField({ key: 'externalParticipantIds', value: participants.externalParticipants.map((p) => p.id) }));
    }
  }, [isOpen, isEditMode, editSixId, catalogSixes, participants, dispatch]);

  // Set current user as default owner when creating a new SIX
  useEffect(() => {
    if (!isOpen || isEditMode) return;
    if (currentUserId === undefined) return;
    if (draft.ownerIds.length > MIN_LEN) return;

    dispatch(setDraftField({ key: 'ownerIds', value: [currentUserId] }));
  }, [isOpen, isEditMode, currentUserId, draft.ownerIds.length, dispatch]);

  useEffect(() => {
    if (!isOpen) return;

    const internalUsersEmpty = internalUsers.length === MIN_LEN;
    const externalUsersEmpty = externalUsers.length === MIN_LEN;

    if (internalUsersEmpty || externalUsersEmpty) {
      void dispatch(fetchCreateSixModalInformationThunk());
    }
  }, [dispatch, externalUsers.length, internalUsers.length, isOpen]);

  const handleClose = useCallback((): void => {
    setIsExternalConfirmationOpen(false);
    dispatch(hideModal());
    dispatch(resetCreateSixState());
    form.reset();
  }, [dispatch, form]);

  const handleCloseExternalConfirmation = useCallback((): void => {
    setIsExternalConfirmationOpen(false);
  }, []);

  const currentWizardStepNumber = resolveWizardStepNumber(createSixStep);
  const isFirstStep = currentWizardStepNumber === 1;
  const isLastStep = currentWizardStepNumber === CREATE_SIX_WIZARD_STEPS[CREATE_SIX_WIZARD_STEPS.length - 1].stepNumber;
  const isNextDisabled = resolveIsNextDisabled(createSixStep, draft);

  const wizardSteps = CREATE_SIX_WIZARD_STEPS.map((stepMeta) => ({
    stepNumber: stepMeta.stepNumber,
    label: stepMeta.label,
    status: resolveWizardStepStatus({
      wizardStepNumber: stepMeta.stepNumber,
      currentWizardStepNumber,
    }),
    isDisabled: stepMeta.stepNumber > currentWizardStepNumber,
  }));

  const handlePrevious = useCallback((): void => {
    if (createSixStep === 'Basics') return;
    dispatch(setStep(resolvePreviousStep(createSixStep)));
  }, [createSixStep, dispatch]);

  const handleConfirmExternalConfirmation = useCallback((): void => {
    setIsExternalConfirmationOpen(false);
    dispatch(setStep(resolveNextStep('External')));
  }, [dispatch]);

  const handleNext = useCallback((): void => {
    // Basics step: validate name with BE before advancing
    if (createSixStep === 'Basics') {
      const trimmedName = draft.name.trim();
      if (
        trimmedName.length === MIN_LEN
        || draft.name.length > CREATE_SIX_NAME_MAX_LENGTH
        || draft.description.length > CREATE_SIX_DESCRIPTION_MAX_LENGTH
      ) {
        return;
      }

      void dispatch(validateSixNameThunk(trimmedName))
        .unwrap()
        .then(() => {
          dispatch(setStep(resolveNextStep(createSixStep)));
        })
        .catch(() => {
          // Error is stored in Redux fieldErrors by the thunk's rejected handler
        });
      return;
    }

    if (createSixStep === 'External' && hasPendingExternalManualEntries) {
      setIsExternalConfirmationOpen(true);
      return;
    }

    if (createSixStep !== 'Review') {
      dispatch(setStep(resolveNextStep(createSixStep)));
      return;
    }

    const payload = buildCreateSixPayloadFromDraft(draft);
    const newExternalUsers = extractNewExternalUsersFromDraft(draft);

    void (async() => {
      const sixId = isEditMode
        ? await (async() => {
          await dispatch(updateSixThunk({ sixId: editSixId, ...payload })).unwrap();
          return editSixId;
        })()
        : (await dispatch(createSixThunk(payload)).unwrap()).sixId;

        dispatch(hideModal());
        dispatch(resetCreateSixState());
        showSuccessToast(isEditMode ? 'SIX updated successfully' : 'SIX created successfully');
        form.reset();
        const createdExternalUserIds = await Promise.all(
        newExternalUsers.map(async(user) => await dispatch(createNewExternalUserThunk(user)).unwrap())
      );

      const allExternalParticipantIds = [
        ...new Set([...payload.externalParticipantIds, ...createdExternalUserIds]),
      ];

      await dispatch(updateCreateSixParticipantsThunk({
        sixId,
        ownerIds: payload.ownerIds,
        internalParticipantIds: payload.internalParticipantIds,
        externalParticipantIds: allExternalParticipantIds,
      })).unwrap();

    })();
  }, [
    createSixStep,
    dispatch,
    draft,
    editSixId,
    form,
    hasPendingExternalManualEntries,
    isEditMode,
  ]);

  return {
    state: {
      isOpen,
      isEditMode,
      isExternalConfirmationOpen,
      createSixStep,
      isSubmitting,
      draft,
      wizardSteps,
      currentWizardStepNumber,
      isFirstStep,
      isLastStep,
      isNextDisabled,
      form,
    },
    handlers: {
      handleClose,
      handleCloseExternalConfirmation,
      handleConfirmExternalConfirmation,
      handlePrevious,
      handleNext,
    },
  };
}
